import {ZIDX} from "../standaloneLibrary.js";
import jQuery from "jquery";
let $ = jQuery.noConflict();

export interface SavedListingEntry {
    uniqueId: number;
    indexId: number;
    listingId: string;
    timestamp: number;
}

export interface GuestCreateResult {
    contactId: number;
    savedListings: SavedListingEntry[];
    hiddenListings: SavedListingEntry[];
}

export class SavedListingRepository {
    private static instance: SavedListingRepository;
    private zidx: ZIDX;
    private static SAVED_KEY = 'zidx.savedListings2';
    private static HIDDEN_KEY = 'zidx.hiddenListings2';
    private static GUEST_CONTACT_KEY = 'zidx.guestContactId';

    // Limit for saves/hides before requiring login/guest account
    private static FREE_LIMIT = 3;

    // In-memory fallback used when localStorage is blocked or unavailable
    private static memoryStore: Map<string, string> = new Map();

    private static safeGet(key: string): string | null {
        try {
            const v = localStorage.getItem(key);
            if (v !== null) {
                return v;
            }
        } catch (e) {
            // localStorage blocked - fall through to memory
        }
        const m = SavedListingRepository.memoryStore.get(key);
        return m === undefined ? null : m;
    }

    private static safeSet(key: string, value: string): void {
        SavedListingRepository.memoryStore.set(key, value);
        try {
            localStorage.setItem(key, value);
        } catch (e) {
            // localStorage blocked or quota exceeded - memory fallback holds the value
        }
    }

    private static safeRemove(key: string): void {
        SavedListingRepository.memoryStore.delete(key);
        try {
            localStorage.removeItem(key);
        } catch (e) {
            // localStorage blocked
        }
    }

    private constructor(zidx: ZIDX) {
        this.zidx = zidx;
        this.migrateOldFormat();
        // Check if we need to migrate guest to real account on page load
        this.checkGuestMigration();
    }

    // Check if a guest account needs to be migrated to a real account
    // This happens when: user is logged in, NOT a guest, but has guestContactId in localStorage
    private async checkGuestMigration() {
        const guestContactId = this.getGuestContactId();
        if (guestContactId === 0) {
            return; // No guest to migrate
        }

        // If logged in as a real account (not guest), migrate
        if (this.zidx.isContactLoggedIn() && !this.isGuestAccount()) {
            console.log("Migrating guest account to real account...");
            await this.migrateGuestToReal();
        }
    }

    public static getInstance(zidx: ZIDX): SavedListingRepository {
        if (!SavedListingRepository.instance) {
            SavedListingRepository.instance = new SavedListingRepository(zidx);
        }
        return SavedListingRepository.instance;
    }

    // Get total count of saved + hidden listings
    public getTotalCount(): number {
        return this.getSavedListings().length + this.getHiddenListings().length;
    }

    // Check if user has reached the free limit
    public hasReachedLimit(): boolean {
        return this.getTotalCount() >= SavedListingRepository.FREE_LIMIT;
    }

    // Check if user is logged in (real or guest)
    public isLoggedInOrGuest(): boolean {
        return this.zidx.isContactLoggedIn();
    }

    // Check if current login is a guest account
    public isGuestAccount(): boolean {
        return this.zidx.getCookie("ZIDXCONTACTGUEST") === "1";
    }

    // Get stored guest contact ID from localStorage
    public getGuestContactId(): number {
        const id = SavedListingRepository.safeGet(SavedListingRepository.GUEST_CONTACT_KEY);
        return id ? parseInt(id, 10) : 0;
    }

    // Store guest contact ID in localStorage
    private setGuestContactId(contactId: number) {
        SavedListingRepository.safeSet(SavedListingRepository.GUEST_CONTACT_KEY, String(contactId));
    }

    // Clear guest contact ID from localStorage
    public clearGuestContactId() {
        SavedListingRepository.safeRemove(SavedListingRepository.GUEST_CONTACT_KEY);
    }

    // Create a guest contact account and sync listings
    public async createGuestAccount(): Promise<GuestCreateResult | null> {
        const savedListings = this.getSavedListings();
        const hiddenListings = this.getHiddenListings();

        return new Promise((resolve) => {
            const obj = {
                id: "contactGuestCreate",
                method: "post",
                ignoreOldRequests: true,
                postObj: {
                    savedListings: JSON.stringify(savedListings),
                    hiddenListings: JSON.stringify(hiddenListings)
                },
                callback: (r: string) => {
                    try {
                        const rs = JSON.parse(r);
                        if (rs.success && rs.data) {
                            const data: GuestCreateResult = JSON.parse(rs.data);
                            // Store guest contact ID
                            this.setGuestContactId(data.contactId);
                            // Update localStorage with server response
                            if (data.savedListings) {
                                this.setSavedListings(data.savedListings);
                            }
                            if (data.hiddenListings) {
                                this.setHiddenListings(data.hiddenListings);
                            }
                            resolve(data);
                        } else {
                            console.error("Failed to create guest account:", rs.errorMessage);
                            resolve(null);
                        }
                    } catch (e) {
                        console.error("Error creating guest account", e);
                        resolve(null);
                    }
                },
                errorCallback: (r: string) => {
                    console.error("Error creating guest account", r);
                    resolve(null);
                },
                url: this.zidx.options.siteDomain + "/contact-guest-create/"
            };
            this.zidx.ajaxRequest(obj);
        });
    }

    // Migrate guest account data to real account
    public async migrateGuestToReal(): Promise<boolean> {
        const guestContactId = this.getGuestContactId();
        if (guestContactId === 0) {
            // No guest account to migrate, just do normal sync
            await this.syncWithServer();
            return true;
        }

        const savedListings = this.getSavedListings();
        const hiddenListings = this.getHiddenListings();

        return new Promise((resolve) => {
            const obj = {
                id: "contactGuestMigrate",
                method: "post",
                ignoreOldRequests: true,
                postObj: {
                    guestContactId: guestContactId,
                    savedListings: JSON.stringify(savedListings),
                    hiddenListings: JSON.stringify(hiddenListings)
                },
                callback: (r: string) => {
                    try {
                        const rs = JSON.parse(r);
                        if (rs.success && rs.data) {
                            const data = JSON.parse(rs.data);
                            // Update localStorage with merged result
                            if (data.savedListings) {
                                this.setSavedListings(data.savedListings);
                            }
                            if (data.hiddenListings) {
                                this.setHiddenListings(data.hiddenListings);
                            }
                            // Clear guest contact ID
                            this.clearGuestContactId();
                            resolve(true);
                        } else {
                            console.error("Failed to migrate guest account:", rs.errorMessage);
                            resolve(false);
                        }
                    } catch (e) {
                        console.error("Error migrating guest account", e);
                        resolve(false);
                    }
                },
                errorCallback: (r: string) => {
                    console.error("Error migrating guest account", r);
                    resolve(false);
                },
                url: this.zidx.options.siteDomain + "/contact-guest-migrate/"
            };
            this.zidx.ajaxRequest(obj);
        });
    }

    // Check if action is allowed (under limit or logged in)
    // Returns true if allowed, false if login prompt should be shown
    public canSaveOrHide(): boolean {
        // If logged in (real or guest), always allowed
        if (this.isLoggedInOrGuest()) {
            return true;
        }
        // If not logged in, check limit
        return !this.hasReachedLimit();
    }

    // Try to save - returns true if successful, false if login needed
    // If limit reached and not logged in, creates guest account
    public async trySave(uniqueId: number, indexId: number, listingId: string): Promise<{success: boolean, needsLogin: boolean}> {
        // If logged in, just save
        if (this.isLoggedInOrGuest()) {
            this.save(uniqueId, indexId, listingId);
            return { success: true, needsLogin: false };
        }

        // Check if under limit
        if (!this.hasReachedLimit()) {
            this.save(uniqueId, indexId, listingId);
            return { success: true, needsLogin: false };
        }

        // Limit reached - show login prompt
        return { success: false, needsLogin: true };
    }

    // Try to hide - returns true if successful, false if login needed
    public async tryHide(uniqueId: number, indexId: number, listingId: string): Promise<{success: boolean, needsLogin: boolean}> {
        // If logged in, just hide
        if (this.isLoggedInOrGuest()) {
            this.hide(uniqueId, indexId, listingId);
            return { success: true, needsLogin: false };
        }

        // Check if under limit
        if (!this.hasReachedLimit()) {
            this.hide(uniqueId, indexId, listingId);
            return { success: true, needsLogin: false };
        }

        // Limit reached - show login prompt
        return { success: false, needsLogin: true };
    }

    // Migrate from old format (array of strings) to new format (array of objects)
    private migrateOldFormat() {
        const oldSavedKey = 'zidx.savedListings';
        const oldHiddenKey = 'zidx.hiddenListings';

        // Migrate saved listings
        const oldSaved = SavedListingRepository.safeGet(oldSavedKey);
        if (oldSaved) {
            try {
                const oldIds: string[] = JSON.parse(oldSaved);
                const newEntries = this.getSavedListings();
                for (const id of oldIds) {
                    const parts = id.split(":");
                    // Old format doesn't have uniqueId, so we set it to 0 - will be resolved on sync
                    const entry: SavedListingEntry = {
                        uniqueId: 0,
                        indexId: parts.length === 2 ? parseInt(parts[0], 10) : 0,
                        listingId: parts.length === 2 ? parts[1] : id,
                        timestamp: Date.now()
                    };
                    if (!this.findEntry(newEntries, entry)) {
                        newEntries.push(entry);
                    }
                }
                this.setSavedListings(newEntries);
                SavedListingRepository.safeRemove(oldSavedKey);
            } catch (e) {
                console.error("Error migrating old saved listings", e);
            }
        }

        // Migrate hidden listings
        const oldHidden = SavedListingRepository.safeGet(oldHiddenKey);
        if (oldHidden) {
            try {
                const oldIds: string[] = JSON.parse(oldHidden);
                const newEntries = this.getHiddenListings();
                for (const id of oldIds) {
                    const parts = id.split(":");
                    const entry: SavedListingEntry = {
                        uniqueId: 0,
                        indexId: parts.length === 2 ? parseInt(parts[0], 10) : 0,
                        listingId: parts.length === 2 ? parts[1] : id,
                        timestamp: Date.now()
                    };
                    if (!this.findEntry(newEntries, entry)) {
                        newEntries.push(entry);
                    }
                }
                this.setHiddenListings(newEntries);
                SavedListingRepository.safeRemove(oldHiddenKey);
            } catch (e) {
                console.error("Error migrating old hidden listings", e);
            }
        }
    }

    private findEntry(entries: SavedListingEntry[], target: SavedListingEntry): SavedListingEntry | undefined {
        return entries.find(e =>
            (e.uniqueId !== 0 && e.uniqueId === target.uniqueId) ||
            (e.indexId === target.indexId && e.listingId === target.listingId)
        );
    }

    public save(uniqueId: number, indexId: number, listingId: string) {
        // Remove from hidden if present
        this.removeFromHidden(uniqueId, indexId, listingId);

        let entries = this.getSavedListings();
        const newEntry: SavedListingEntry = {
            uniqueId,
            indexId,
            listingId,
            timestamp: Date.now()
        };

        const existing = this.findEntry(entries, newEntry);
        if (!existing) {
            entries.push(newEntry);
            this.setSavedListings(entries);
        }
    }

    public hide(uniqueId: number, indexId: number, listingId: string) {
        // Remove from saved first
        this.removeFromSaved(uniqueId, indexId, listingId);

        let entries = this.getHiddenListings();
        const newEntry: SavedListingEntry = {
            uniqueId,
            indexId,
            listingId,
            timestamp: Date.now()
        };

        const existing = this.findEntry(entries, newEntry);
        if (!existing) {
            entries.push(newEntry);
            this.setHiddenListings(entries);
        }
    }

    // Navigate back to search results - call this after server sync completes
    public navigateBackToSearch() {
        let backToSearch = $(".zidxBackToSearch", this.zidx.peakidxShadowRoot);
        if (backToSearch.length == 0) {
            window.location.href = this.zidx.getPrefixURL("/results/");
        } else {
            backToSearch.trigger("click");
        }
    }

    public removeFromSaved(uniqueId: number, indexId: number, listingId: string) {
        let entries = this.getSavedListings();
        const target: SavedListingEntry = { uniqueId, indexId, listingId, timestamp: 0 };
        entries = entries.filter(e => !this.entriesMatch(e, target));
        this.setSavedListings(entries);
    }

    public removeFromHidden(uniqueId: number, indexId: number, listingId: string) {
        let entries = this.getHiddenListings();
        const target: SavedListingEntry = { uniqueId, indexId, listingId, timestamp: 0 };
        entries = entries.filter(e => !this.entriesMatch(e, target));
        this.setHiddenListings(entries);
    }

    private entriesMatch(a: SavedListingEntry, b: SavedListingEntry): boolean {
        if (a.uniqueId !== 0 && b.uniqueId !== 0 && a.uniqueId === b.uniqueId) {
            return true;
        }
        return a.indexId === b.indexId && a.listingId === b.listingId;
    }

    public isSaved(listingId: string, uniqueId?: number): boolean {
        if(typeof listingId=="undefined"){
            return true;
        }
        const entries = this.getSavedListings();
        for (const entry of entries) {
            if (uniqueId && entry.uniqueId === uniqueId) {
                return true;
            }
            if (entry.listingId === listingId) {
                return true;
            }
            // Handle old format comparison
            const inputParts = listingId.split(":");
            if (inputParts.length === 2) {
                if (entry.indexId === parseInt(inputParts[0], 10) && entry.listingId === inputParts[1]) {
                    return true;
                }
            }
        }
        return false;
    }

    public isHidden(listingId: string, uniqueId?: number): boolean {
        if(typeof listingId=="undefined"){
            return true;
        }
        const entries = this.getHiddenListings();
        for (const entry of entries) {
            if (uniqueId && entry.uniqueId === uniqueId) {
                return true;
            }
            if (entry.listingId === listingId) {
                return true;
            }
            // console.log("isHidden", listingId);
            const inputParts = listingId.replace("-", ":").split(":");
            if (inputParts.length === 2) {
                if (entry.indexId === parseInt(inputParts[0], 10) && entry.listingId === inputParts[1]) {
                    return true;
                }
            }
        }
        return false;
    }

    public getSavedListings(): SavedListingEntry[] {
        const value = SavedListingRepository.safeGet(SavedListingRepository.SAVED_KEY);
        return (value && JSON.parse(value)) || [];
    }

    public getHiddenListings(): SavedListingEntry[] {
        const value = SavedListingRepository.safeGet(SavedListingRepository.HIDDEN_KEY);
        return (value && JSON.parse(value)) || [];
    }

    private setSavedListings(entries: SavedListingEntry[]) {
        SavedListingRepository.safeSet(SavedListingRepository.SAVED_KEY, JSON.stringify(entries));
    }

    private setHiddenListings(entries: SavedListingEntry[]) {
        SavedListingRepository.safeSet(SavedListingRepository.HIDDEN_KEY, JSON.stringify(entries));
    }

    // Legacy method for backwards compatibility - returns indexId:listingId format
    public getListingIds(): string[] {
        return this.getSavedListings().map(e => `${e.indexId}:${e.listingId}`);
    }

    public getHiddenListingIds(): string[] {
        return this.getHiddenListings().map(e => `${e.indexId}:${e.listingId}`);
    }

    // Backwards-compatible wrapper methods that parse "indexId:listingId" string format
    public saveByStringId(dataId: string, uniqueId: number = 0) {
        const parts = dataId.split(":");
        const indexId = parts.length === 2 ? parseInt(parts[0], 10) : 0;
        const listingId = parts.length === 2 ? parts[1] : dataId;
        this.save(uniqueId, indexId, listingId);
    }

    public removeByStringId(dataId: string, uniqueId: number = 0) {
        const parts = dataId.split(":");
        const indexId = parts.length === 2 ? parseInt(parts[0], 10) : 0;
        const listingId = parts.length === 2 ? parts[1] : dataId;
        this.removeFromSaved(uniqueId, indexId, listingId);
    }

    public hideByStringId(dataId: string, uniqueId: number = 0) {
        const parts = dataId.split(":");
        const indexId = parts.length === 2 ? parseInt(parts[0], 10) : 0;
        const listingId = parts.length === 2 ? parts[1] : dataId;
        this.hide(uniqueId, indexId, listingId);
    }

    // Sync with server - called after login and when viewing saved/hidden pages
    // For guest-to-real migration, use migrateGuestToReal() instead
    public async syncWithServer(): Promise<void> {
        if (!this.zidx.isContactLoggedIn()) {
            return;
        }

        const savedListings = this.getSavedListings();
        const hiddenListings = this.getHiddenListings();

        return new Promise((resolve) => {
            const obj = {
                id: "contactSyncListings",
                method: "post",
                ignoreOldRequests: true,
                postObj: {
                    savedListings: JSON.stringify(savedListings),
                    hiddenListings: JSON.stringify(hiddenListings)
                },
                callback: (r: string) => {
                    try {
                        const rs = JSON.parse(r);
                        if (rs.success && rs.data) {
                            const data = JSON.parse(rs.data);
                            // Update localStorage with merged server response
                            if (data.savedListings) {
                                this.setSavedListings(data.savedListings);
                            }
                            if (data.hiddenListings) {
                                this.setHiddenListings(data.hiddenListings);
                            }
                        }
                    } catch (e) {
                        console.error("Error syncing listings with server", e);
                    }
                    resolve();
                },
                errorCallback: (r: string) => {
                    console.error("Error syncing listings", r);
                    resolve();
                },
                url: this.zidx.options.siteDomain + "/contact-sync-listings/"
            };
            this.zidx.ajaxRequest(obj);
        });
    }
}
